import { useMemo } from 'react';
import { PolicyRevision, DiffResult, DiffHunk } from '../types';
import { calculateStructuredDiff } from '../services/diffCalculator';

interface UseDiffViewerProps {
  baseRev?: PolicyRevision;
  targetRev?: PolicyRevision;
  ignoreWhitespace?: boolean;
}

export const useDiffViewer = ({ baseRev, targetRev, ignoreWhitespace = false }: UseDiffViewerProps) => {
  const diffResult = useMemo<DiffResult | null>(() => {
    if (!targetRev) return null;

    // Check if targetRev has pre-computed structured diff
    const structDiff = targetRev.structured_diff || targetRev.structuredDiff;
    if (structDiff && structDiff.hunks && structDiff.hunks.length > 0) {
      return {
        baseRevisionId: baseRev?.id || targetRev.previous_revision_id || targetRev.previousRevisionId || 'initial',
        targetRevisionId: targetRev.id,
        companyId: targetRev.company_id || targetRev.companyId || 'company',
        policyType: (targetRev.policy_type || targetRev.policyType || 'terms_of_service') as any,
        stats: structDiff.stats || {
          linesAdded: (structDiff as any).total_additions || (structDiff as any).linesAdded || 0,
          linesRemoved: (structDiff as any).total_deletions || (structDiff as any).linesRemoved || 0,
          linesModified: (structDiff as any).total_modifications || (structDiff as any).linesModified || 0,
          wordsAdded: 0,
          wordsRemoved: 0,
          similarityRatio: 1.0,
          totalOldLines: 0,
          totalNewLines: 0,
        },
        hunks: structDiff.hunks as DiffHunk[],
      };
    }

    // Calculate dynamically from actual policy text
    const oldText = baseRev?.rawText || baseRev?.raw_text || baseRev?.textExcerpt || baseRev?.text_excerpt || '';
    const newText = targetRev.rawText || targetRev.raw_text || targetRev.textExcerpt || targetRev.text_excerpt || '';

    if (oldText || newText) {
      return calculateStructuredDiff(oldText, newText, baseRev?.id, targetRev.id, ignoreWhitespace);
    }

    return null;
  }, [baseRev, targetRev, ignoreWhitespace]);

  return { diffResult, loading: false, error: null };
};
